import type { PostLockUnlockResource } from '@/models/case';
import { StudyModel, type NetworkStudy, type StudyFlags, type StudyImportDiff, type INewStudy, type StudySchemaExport } from '@/models/study';
import { StudySettingsModel, type NetworkStudySettings, WebhookModel, BillingModel,
  type NetworkBillingSettings, type StudySettingsUpdateData } from '@/models/studySettings';
import { BACKEND_URL, signedApiClient, signingString } from '@/services/apiClient';
import axios from 'axios';

class StudyService {

  private investigationEndpoint: string = '';

  initService(investigationEndpoint: string) {
    this.investigationEndpoint = investigationEndpoint;
  }

  async getStudies() {
    const uri = this.investigationEndpoint;
    const apiClient = signedApiClient('GET', uri);
    const studiesResponse = await apiClient.get(uri);
    const networkStudies: NetworkStudy[] = studiesResponse.data.investigations;
    return networkStudies.map(study => StudyModel.mapFromBackend(study));
  }

  async getStudy(identifier: string) {
    const uri = `${this.investigationEndpoint}/${identifier}`;
    const apiClient = signedApiClient('GET', uri);
    const studyResponse = await apiClient.get(uri);
    const networkStudy = studyResponse.data.details;
    return StudyModel.mapFromBackend(networkStudy);
  }

  async getStudyPermissions(identifier: string) {
    const uri = `${this.investigationEndpoint}/${identifier}/permissions`;
    const apiClient = signedApiClient('GET', uri);
    const permissionResponse = await apiClient.get(uri);
    return permissionResponse.data.permissions;
  }

  async getInvestigation(identifier: string) {
    const uri = `${this.investigationEndpoint}/${identifier}`;
    const apiClient = signedApiClient('GET', uri);

    const response = await apiClient.get(uri);
    const model: NetworkStudySettings = response.data;
    const formattedModel = StudySettingsModel.mapFromBackend(model);
    return formattedModel;
  }

  async getWebhooks(identifier: string) {
    const uri = `${this.investigationEndpoint}/${identifier}/webhooks`;
    const apiClient = signedApiClient('GET', uri);

    const response = await apiClient.get(uri);
    const formattedModel: WebhookModel = response.data;
    return formattedModel;
  }

  async getBilling(identifier: string) {
    const uri = `${this.investigationEndpoint}/${identifier}/billing`;
    const apiClient = signedApiClient('GET', uri);

    const response = await apiClient.get(uri);
    const model: NetworkBillingSettings = response.data;
    const formattedModel = BillingModel.mapFromBackend(model);
    return formattedModel;
  }

  async patchDetails(details: StudySettingsUpdateData) {
    const { identifier, ...rest } = details;
    const uri = `${this.investigationEndpoint}/${identifier}/details`;
    const apiClient = signedApiClient('PATCH', uri);

    const response = await apiClient.patch(uri, rest);
    return response.data;
  }

  async patchApiKey(identifier: string, apiKeyExpiration: number) {
    const uri = `${this.investigationEndpoint}/${identifier}/apiKey`;
    const apiClient = signedApiClient('PATCH', uri);

    let data = {};
    if (apiKeyExpiration) {
      data = {
        expirationInMonths: apiKeyExpiration
      };
    }
    const response = await apiClient.patch(uri, data);
    return response.data;
  }

  async lockUnlockResources(studyId: string, payload: PostLockUnlockResource) {
    const uri = `${this.investigationEndpoint}/${studyId}/lock-items`;
    const apiClient = signedApiClient('POST', uri);
    return await apiClient.post(uri, payload);
  }

  async archiveResources(studyId: string, payload: PostLockUnlockResource) {
    const uri = `${this.investigationEndpoint}/${studyId}/archive-items`;
    const apiClient = signedApiClient('POST', uri);
    return await apiClient.post(uri, payload);
  }
  async postStudy(payload: INewStudy) {
    const uri = `${this.investigationEndpoint}`;
    const apiClient = signedApiClient('POST', uri);
    return await apiClient.post(uri, payload);
  }

  async getStudyExport(studyId: string, payload: StudySchemaExport) {
    const uri = `study/${studyId}/export`;
    const apiClient = signedApiClient('POST', uri);
    const blob = await apiClient.post<Blob>(uri, payload, { responseType: 'blob' });
    const filename = decodeURIComponent(
      blob.headers['content-disposition']
        .replace('attachment; filename=', '')
    );
    const filetype = blob.headers['content-type'] as string;
    const systemBlob = new Blob([blob.data]);
    const file = new File([systemBlob], filename, { type: filetype });
    const url = window.URL.createObjectURL(file);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode?.removeChild(link);
  }

  async importAndValidate(studyId: string, importFile: File, customDataDictionary?: string): Promise<StudyImportDiff> {
    const uri = `${BACKEND_URL}/study/${studyId}/import-diff`;
    const signingObj = signingString('POST', uri);

    const form = new FormData();
    form.append('file', importFile);
    if (customDataDictionary) {
      form.append('customDataDictionary', customDataDictionary);
    }

    const res = await axios({
      url: uri,
      method: 'POST',
      data: form,
      headers: {
        'X-Date': signingObj.date,
        Authorization: signingObj.string
      }
    });
    return res.data;
  }

  async importStudy(studyId: string, importData: StudyImportDiff) {
    const uri = `study/${studyId}/import`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, importData);
  }

  async importStudyForm(studyId: string, filename: string, filepath: string): Promise<StudyImportDiff> {
    const response = await fetch(filepath);
    const blob = await response.blob();

    // Convert the Blob to a File object
    const file = new File([blob], filename, { type: blob.type });

    const uri = `${BACKEND_URL}/study/${studyId}/import-new-form`;
    const signingObj = signingString('POST', uri);

    const form = new FormData();
    form.append('file', file);

    const res = await axios({
      url: uri,
      method: 'POST',
      data: form,
      headers: {
        'X-Date': signingObj.date,
        Authorization: signingObj.string
      }
    });
    return res.data;
  }

  async updateFlags(studyId: string, flags: StudyFlags) {
    const uri = `study/${studyId}/flags`;
    const apiClient = signedApiClient('PATCH', uri);
    const response = await apiClient.patch(uri, flags);
    return response.data;
  }

  async joinStudy(studyId: string, token?: string) {
    const uri = `investigation/${studyId}/join`;
    const apiClient = signedApiClient('POST', uri);
    const response = await apiClient.post(uri, {token});
    return response.data;
  }
}

export default new StudyService();
